import { formatter } from "../../shared/ts/formatter";
import AnnuityType from "./annuityType";
import Constants from "./constants";

export default class HelpText {
  public static TextNotYetDefined(): Array<string> {
    return [
      "The help content on this subject is still to be defined."
    ];
  }

  public static ReqmntAfterTaxIncome(): Array<string> {
    return [
      "This is in line with the new <a href='https://www.retirementlivingstandards.org.uk/details' target='_blank'>Retirement Living Standards</a> figures adjusted for inflation. It's difficult knowing how much you will need after tax in retirement. To help, the Retirement Living Standards have created living standards so you can see what you may need.",
      "By answering the two questions in this page, we will give you an estimate, adjusted for inflation, based on their research. You can either use this figure or overwrite it with any other figure you prefer.",
      "Later on, in our calculations we will see if you are able to get what you want. If not you can solve to see how much you will need to reduce this by to avoid risking running out of money.",
    ];
  }

  public static ReqmntFirstFewYears(): Array<string> {
    return [
      "Many people also like to have a bit more extra income in the first few years of retirement, if this is the case enter the amount here and we will see if this possible.",
      "This may be for added expenses such as nice holidays in the early years of retirement. Or added costs, if you have a few years left on the mortgage for example."
    ];
  }

  public static ReqmntInflation(): Array<string> {
    return [
      "£1,000 ten years after retirement won't buy as much as £1,000 at retirement because of inflation.",
      "Would you like some, or all, future payments to increase at 2.5% to try and keep the same amount of spending power in future as you would have at retirement?",
    ];
  }

  public static ReqmntInflationDuration(): Array<string> {
    return [
      "Many retirees spend more money at the beginning of retirement and less in later years when they may be less active.",
      "If you choose no increases after 75 this means you can get a higher starting income with the same size pot. Your income will buy less each year after 75, but you may expect to spend less anyway.",
    ];
  }

  public static PensionPotsCurrentValue(): Array<string> {
    return [
      "The estimated current value is calculated by taking the valuation amount and adding future assumed investment returns between the valuation date and the current date.",
      "In addition, all future expected monthly contributions are also added and increased by the assumed investment growth between the date these are paid and the current date.",
      "If the valuation date is recent then the current value will match the valuation."
    ];
  }

  public static PensionPotsEstValAtRetirement(): Array<string> {
    const taxFreeCashLimit = formatter.formatCurrency(Constants.TaxFreeCashLimit);
    return [
      "The estimated value at retirement is calculated by taking the valuation amount and adding future assumed investment returns between the valuation date and the target retirement date.",
      "In addition, all future expected monthly contributions are also added and increased by the assumed investment growth between the date these are paid and the target retirement date.",
      `If you chose to take tax free cash as a lump sum, the figure shown will be immediately reduced by 25% up to a maximum of ${taxFreeCashLimit} (as this is the current maximum lump sum most people can take).`,
      `You may have a higher limit if you have previously registered for Lifetime Allowance protection, but we have assumed ${taxFreeCashLimit} is taken in our calculations. If you have a higher limit, we would recommend seeking financial advice to ensure you do not lose this protection.`
    ];
  }

  public static savingsPots(): Array<string> {
    return [
      "If you have any Savings, ISAs or other Investments which you want to put towards your retirement, enter the current value for each one.",
      "If you intend to continue to build these up with more contributions you can add the monthly amount you expect to pay also.",
    ];
  }

  public static otherCash(): Array<string> {
    return [
      "You may expect one or more cash lump sums in future. If you want to save this money and put some (or all) of it towards your retirement income, rather than spend the money or use it to pay off debts, this will be added to your savings pots.",
      "Enter the amount you expect to get and save and when you expect to get this. Common expected lump sums are;",
      "<i>Downsize your main home</i>",
      "If you are planning to downsize your house or release some of the profit you've made on it, then enter the expected amount you will receive after you have sold this and purchased a smaller home, or the amount of profit you expect to take out, and when you expect this.",
      "<i>Equity Release</i>",
      "If you want to extract equity from your main home and don't want to move or downsize, then Equity Release could be the answer. By borrowing against your home's value in retirement you can continue to live there and extract some of the equity within it.",
      "<i>Selling a second home or an investment</i>",
      "If you are planning to sell a second house or an investment, then enter the expected profit after any taxes and mortgages you will need to pay and when you expect to get this.",
      "<i>Tax free cash from pensions</i>",
      "You may have some tax free cash from your final salary pensions, or want to save and use some (or all) of the tax free cash from your defined contribution pension pots if you are taking 25% tax free cash at retirement.",
    ];
  }

  public static otherIncome(): Array<string> {
    return [
      "You may also expect some other non pension income after you retire for a few years. This may come from some part time work for a few years after you start to take a retirement income, or rent from a second home as an example.",
      "Enter the amount, the dates you expect this from and to and whether you expect this to increase each year with inflation.",
    ];
  }

  public static incomeAndPotsCharts(): Array<string> {
    return [
      "This chart tells you if by taking income from your pensions and savings pots on top of everything else you have, whether you can get what you want after tax.",
      "If you cannot, it will show expected shortfalls. You can try to fix these using the Take Less and Pay More solve buttons.",
      "Clicking on any year will also show you a chart of the breakdown of all the different parts of your total income in that year.",
    ];
  }

  public static pensionContributions(): Array<string> {
    return [
      "Where you pay contributions over a period which is not monthly, your current CPT total contributions (your own plus your employers) have been converted into a monthly amount for illustration purposes only.",
    ];
  }

  public static growthProjections(): Array<string> {
    return [
      "Where you have previously saved a plan and entered growth rates manually, we have used the previous growth rates you entered. ",
      "You can change the growth assumptions if wanted and these will change.",
      "<strong>Tell me more about expected growth rates</strong>",
      "There is always a trade off between risk and return. The more risk you take the more return you may be expected to get. This makes perfect sense as why would you take more risk if you did not expect more return.",
      "However it is just that, an expectation, there is no guarantee you will get more return. Given this, the lower the growth assumption you choose the more chance you have of getting it.",
      "A typical balanced long term investment strategy may be around 50% in world stock markets and 50% in less risky government debt. Based on current market conditions, a reasonable expectation (which is not guaranteed) is that on average you may get 5.5% a year after any fund charges from this type of balanced investment strategy.",
      "You then have to pay additional provider charges, which as an average may be another 0.5% reduction to overall expected growth.",
      "Therefore with a typical balanced long term investment strategy there may be a 50% chance of achieving a net return (after all charges) of 5% per year on average.",
      "<strong>Chance of achieving the growth rate chosen</strong>",
      "There is a much higher chance of achieving a lower rate, (so a lower risk of not doing so).",
      "There is a much lower chance of achieving a higher rate, (so a higher risk of not doing so).",
      "You can choose any rate to use between 0% and 8% in your modelling. We give indications of what may be a sensible assumption for a low, medium and high risk of not achieving the growth rates chosen.",
      "So a low risk assumption to use may be 3% and a high risk 7% as examples.",
      "We also show if any rate chosen looks inconsistent with the risk in achieving it. Choosing an 8% growth rate is clearly not consistent with having a low chance that it will not happen.",
      "<strong>Testing</strong>",
      "In our Test page we show if the plan would still be expected to avoid expected shortfalls if you get 1% lower average growth than the growth rates you selected.",
      "Finally we show what might happen if there was one off large 20% fall in pensions and savings pots just after retirement, but other future years are in line with the average. Large falls in value just after retirement can be hard to recover from.",
      "<strong>Tracking</strong>",
      "It's very important to keep track of your plan over time and update the fund values over time to see if your plan is still expected to show no future shortfalls.",
      "<strong>Further help</strong>",
      'For more help understanding investments, growth rates and the risks involved <a href="https://www.moneyhelper.org.uk/en/savings/investing/investing-beginners-guide" target="_blank">try this link to Moneyhelper</a> which can give you more information.',
      'If you do not feel confident in making these decisions yourself you could contact a regulated financial adviser for advice. Again <a href="https://www.moneyhelper.org.uk/en/getting-help-and-advice/financial-advisers/do-you-need-a-financial-adviser" target="_blank">another Moneyhelper link</a> can help you with understanding more about financial advice and whether you need it.',
    ];
  }

  public static guaranteedIncome(annuityType: AnnuityType): Array<string> {
    if (annuityType == AnnuityType.GuaranteedIncome) {
      return [
        "This is an annual guaranteed income which will be paid every month into your pension pot for as long as you live. You can choose to withdraw it to support your income plan, or keep it invested in your pension pot. Either way, no matter what happens it will be paid in, every year, for life.",
        "The amounts shown are just illustrations. The actual amount you would receive could be higher or lower. Actual rates will depend on your age when you apply, plus they will be personalised to you, so other factors such as your health, lifestyle and where you live will determine the final rate.",
      ];
    }

    return [
      "This is an annual guaranteed income added to your other income.",
      "The amounts shown are just illustrations. The actual amount you would receive could be higher or lower. Actual rates will depend on your age when you apply, plus they will be personalised to you, so other factors such as your health, lifestyle and where you live will determine the final rate.",
    ];
  }

  public static everythingYouWant(): Array<string> {
    return [
      "Everything you want during retirement is set out below. These amounts will change if you use the take less solve button.",
      "Also shown is how much in total you are currently paying into your defined contribution pension pots.",
    ];
  }

  public static howWeCalcTests(): Array<string> {
    return [
      "These are the details of the tests we have carried out to see if your plan will work. You don't need to solve these if they are not a concern. We try to help by showing, where possible, what actions can be taken to try and reduce the risk of these occuring.",
      "<strong>Lower growth test</strong>",
      "We have tested if you pay 1% a year more in charges (and don't get any extra growth) or pay the same charges but get 1% a year less growth, would you be expected to run out of money?",
      "<strong>Live longer test</strong>",
      "We have tested if you live " + Constants.HelpTextTokens.numberOfExtraYearsInLiveLongerTest + " years longer than expected, would you be expected to run out of money?",
      "<strong>Market crash test</strong>",
      "We have tested if your savings and pension pots both fall 20% just after you retire, would you be expected to run out of money?",
      "<strong>Need care test</strong>",
      "We have tested if you need £40,000 a year for the last two years of your life to pay for some care, would you be expected to run out of money?",
      "In all cases if you have put aside some money in a protected pot, we use this protected pot also in seeing if your plan will work. Under all tests, these are done individually and everything else remains as expected.",
    ];
  }

  public static livePensionPots(): Array<string> {
    return [
      "The ability to bring in 'live' values is intended to help you see all of your pots in one place and also check the updated charts and outcomes based on these values. To provide these values we use a secure FCA registered dashboard tech provider called PensionLab.",
      "The values shown in our dashboard are expected to be within 0-3 days of their actual value (i.e. what you see in your provider's site or app itself). These timings will vary from provider to provider depending on exactly when they update your funds.",
      "Some providers may intermittently have issues in providing up to date data, for instance if they significantly change or upgrade their site or app, or if you have Multi Factor Authority security set up (like a security text message when you log in for example).",
    ];
  }

  public static healthStateRatherNotSay(): Array<string> {
    return [
      "We will use average life expectancy in our modelling, but any insurer will assume you are fit and healthy.",
      "This may mean guaranteed income type insurance products would pay less income than if you were willing to provide them with some information on your health."
    ];
  }

  public static healthStatePerfect(): Array<string> {
    return [
      "An example of this would be anyone who has never smoked, drinks 10 or less alcohol units per week, is fit and active with no health issues at all.",
    ];
  }

  public static healthStateMinor(): Array<string> {
    return [
      "An example of this would be anyone who has never smoked, but drinks alcohol most days of the week and perhaps takes some medication for high blood pressure or raised cholesterol.",
    ];
  }

  public static healthStateModerate(): Array<string> {
    return [
      "An example of this would be anyone who has smoked regularly for over 10 years, drinks alcohol most days of the week and perhaps takes some medication for say high blood pressure or raised cholesterol.",
    ];
  }

  public static healthStateMajor(): Array<string> {
    return [
      "An example of this would be anyone who has serious health issues which require ongoing medication and treatment.",
      "We will use a lower life expectancy in our modelling, but any insurer would need to understand more on the specific details if you wished to purchase a product like a guaranteed income from them."
    ];
  }

  public static todaysMoney(): Array<string> {
    return [
      "Inflation means we are able to buy less with the same money in future as we can now. A loaf of bread in 1971 cost 10p. Now you won't get a slice for that!",
      "No one knows what inflation will be in future, but if we assume it is 2.5% a year on average (yes it has been higher lately, but 2.5% is just above the Government's long term target), then we can show you what your future income may be worth in today's money.",
    ];
  }

  public static finishTaxEfficiency(): Array<string> {
    return [
      'You pay tax when you take money from your pension pots, but not when you take money from your savings.',
      'We assume you always take enough money from your pension pots before touching your savings to use up all your tax free allowance in any year as you can take this money without paying tax. This lowers the total tax you can expect to pay over the course of your plan.'
    ];
  }

  public static planTableEverythingElse(): Array<string> {
    return [
      'This is the total of your State Pension, final salary pensions and other income in that year.',
      'Where everything else provides more than what you want after tax in any year, only the amount needed is shown.',
      'Any additional income above this is assumed to be put back into your savings and used later if needed.'
    ];
  }

  public static planTableTaxToPay(): Array<string> {
    return [
      'This is the estimated tax you will need to pay over the whole year.',
      'See the assumptions tab for more details on how estimated tax is calculated.',
    ];
  }

  public static planTableTotal(): Array<string> {
    return [
      'This is the total of your income from your pensions and savings pots plus everything else less any estimated tax you need to pay.',
      'This should equal what you want after tax each year, if there were no shortfalls shown.',
      'As the monthly payments are all rounded to the nearest pound and then added up, there may be a few pounds difference.'
    ];
  }

  public static taxFreeLumpSumHoldingFund(): Array<string> {
    return [
      'As you expect to take 25% tax free cash at retirement, you will need to have 25% in cash (the Money Fund) at retirement. Now and '
      + 'at any future review up to retirement you can choose whether to put that 25% in the Money Fund.'
    ];
  }

  public static DBOptionsStandardPensionOption(): Array<string> {
    return [
        "This is the average annual increase you may expect to receive in future from your Standard Pension Option.",
        "It is based on the period from your planned retirement age and the life expectancy shown behind this popup.",
        "It takes into account the rate of inflation you have selected on the inflation slider below the chart, as well as the different parts of your pension which may receive different rates of annual increases.",
    ];
  }

  public static DBOptionsPIE(): Array<string> {
    return [
        "This is the average annual increase you may expect to receive in future from your Pension Increase Exchange option. It will be lower than the average annual increase from your Standard Pension Option as the PIE option allows you to exchange some of your future yearly increases for a higher starting pension.",
        "It is based on the period from your planned retirement age and the life expectancy shown behind this popup.",
        "It takes into account the rate of inflation you have selected on the inflation slider below the chart, as well as the different parts of your pension which may receive different rates of annual increases.",
    ];
  }
}
